import React, { useEffect, useState } from 'react';
import jwtAxios from '@projectK/services/auth/JWT';
import copy from 'copy-to-clipboard';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import { isParent } from '@projectK/context/RolesProvider';
import { useToast } from '@projectK/context/ToastProvider';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import PropTypes from 'prop-types';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { MyScheduleDashboardMain } from '@projectK/MainComponents';
import { timeZoneDate } from '@projectK/modules/apps/DateFormat';
 
const themeColors = [
  '#2563EB',
  '#9333EA',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#3B82F6',
  '#8B5CF6',
  '#14B8A6',
  '#84CC16',
  '#F97316',
];
 
const ActivitySchedules = ({
  list,
  loading,
  setDateFilter,
  setSearchQuery,
  setShowAll,
  showAll,
  useDateFilter,
  fetchActivitySessions,
  fetchMySchedule,
  fetchUserProduct,
  fetchNext7DaysSessionCount,
  setLoading,
}) => {
  const [view, setView] = useState('list');
  const { isMobile, mobileMode } = useMobileSettings();
  const showToast = useToast();
  const { user } = useAuthUser();
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [sessionToCancel, setSessionToCancel] = useState(null);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [googleSyncStatus, setGoogleSyncStatus] = useState('idle');
  const [outlookSyncStatus, setOutlookSyncStatus] = useState('idle');
  const [lastSynced, setLastSynced] = useState(null);
  const fetchDomains = async (userData) => {
    try {
      const response = await jwtAxios.get(
        `/auth/customer-domains/${userData.email}`,
      );
      const { data } = response;
      const domain = data?.list?.find(
        (d) => d.customer_id === userData.customerId,
      );
      setCurrentDomain(domain);
    } catch (error) {
      showToast(error?.response?.data?.error, 'error');
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchDomains(user);
    }
  }, [user?.email]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
 
  const openConfirmDialog = (session) => {
    setSessionToCancel(session);
    setIsConfirmDialogOpen(true);
  };
  const closeConfirmDialog = () => {
    setSessionToCancel(null);
    setIsConfirmDialogOpen(false);
  };
 
  const onSearchActivity = (e) => setSearchQuery(e.target.value);
 
  const toggleShowAll = () => {
    setShowAll(!showAll);
    setDateFilter(!useDateFilter);
  };
 
  const handleCancelUp = async (userId, id, product_id) => {
    try {
      setLoading(true);
      await jwtAxios.delete(`/user-session`, {
        params: { user_id: userId, activity_session_id: id, product_id },
      });
      setLoading(false);
      fetchActivitySessions();
      fetchMySchedule();
      if (!isMobile) {
        fetchUserProduct();
        fetchNext7DaysSessionCount();
        infoViewActionsContext.showMessage('Cancelled session successfully.');
      } else {
        showToast('Cancelled session successfully.', 'success', mobileMode);
      }
      const isParentUser = isParent(user);
      jwtAxios.get(
        `/events/update/user/calender`,
        isParentUser
          ? {
              params: {
                user_id: userId,
              },
            }
          : {},
      );
    } catch (error) {
      setLoading(false);
      if (isMobile) {
        showToast(error?.response?.data?.error, 'error', mobileMode);
      } else {
        infoViewActionsContext.fetchError(error?.response?.data?.error);
      }
    }
  };
  
let baseUrl = window.location.hostname;
if(isMobile && currentDomain){
  baseUrl = `${currentDomain.customer_sub_domain}`
}
  const trimmedBaseUrl = baseUrl.replace(/^https?:\/\//, '');
  
  const handleCopyToClipboard = async () => {
    try {
      const response = await jwtAxios.get(`/events/calendar-feed-url/${user.id}`, {
        withCredentials: true,
      });
      const trimmedUrl = response.data?.url;
      if (!trimmedUrl) {
        infoViewActionsContext.fetchError('Unable to build calendar link');
        return;
      }
      copy(trimmedUrl);
      infoViewActionsContext.showMessage('Link copied successfully!');
    } catch (error) {
      infoViewActionsContext.fetchError(
        error?.response?.data?.error || 'Failed to copy calendar link',
      );
    }
  };
 

  const handleGoogle = async () => {
    try {
      const returnPath = '/dashboard';
      localStorage.setItem('calendarSyncInProgress', 'true');
      setIsModalOpen(false);
      const response = await jwtAxios.get('/events/auth/google/initiate', {
        params: { returnPath },
        withCredentials: true, 
      });

      const { url } = response.data;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      console.error('Failed to initiate Google auth', err);
    }
  };

  const checkSyncStatus = async () => {
    try {
      const googleRes = await jwtAxios.get('/events/google/sync-status', {
        withCredentials: true,
      });
      const outlookRes = await jwtAxios.get('/events/outlook/sync-status', {
        withCredentials: true,
      });
      let googleStatus = googleRes.data.status;
      let outlookStatus = outlookRes.data.status;

      if (googleStatus === 'failed' && !googleRes.data.lastSynced) {
        googleStatus = 'idle';
      }
      if (outlookStatus === 'failed' && !outlookRes.data.lastSynced) {
        outlookStatus = 'idle';
      }

      setGoogleSyncStatus(googleStatus);
      setOutlookSyncStatus(outlookStatus);

      // Handle last synced time
      const latestSync =
        googleRes.data.lastSynced || outlookRes.data.lastSynced;
      if (latestSync) {
        const { startDate } = timeZoneDate(latestSync);
        setLastSynced(startDate.toFormat('MMM dd, yyyy hh:mm a'));
      }
    } catch (err) {
      console.error('Failed to check sync status', err);
      setGoogleSyncStatus('idle');
      setOutlookSyncStatus('idle');
    }
  };
  

  useEffect(() => {
    // On open, try to fetch initial sync status (if already connected)
    checkSyncStatus();
  }, [isModalOpen]);

    const handleOutLook = async () => {
      try {
        const returnPath = '/dashboard';
        localStorage.setItem('calendarSyncInProgress', 'true');
        setIsModalOpen(false);
        const response = await jwtAxios.get('/events/auth/outlook/initiate', {
          params: { returnPath },
          withCredentials: true,
        });
  
        const { url } = response.data;
        if (url) {
          window.location.href = url; // Redirect to Microsoft login
        }
      } catch (err) {
        console.error('Failed to initiate Outlook auth', err);
        showToast('Failed to connect Outlook calendar', 'error');
      }
    };
  
  return (
    <MyScheduleDashboardMain
      list={list}
      loading={loading}
      onSearchActivity={onSearchActivity}
      openModal={openModal}
      setView={setView}
      view={view}
      themeColors={themeColors}
      user={user}
      openConfirmDialog={openConfirmDialog}
      toggleShowAll={toggleShowAll}
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      baseUrl={trimmedBaseUrl}
      handleCopyToClipboard={handleCopyToClipboard}
      handleGoogle={handleGoogle}
      handleOutLook={handleOutLook}
      isConfirmDialogOpen={isConfirmDialogOpen}
      closeConfirmDialog={closeConfirmDialog}
      handleCancelUp={handleCancelUp}
      sessionToCancel={sessionToCancel}
      companyTheme={companyTheme}
      showAll={showAll}
      outlookSyncStatus={outlookSyncStatus}
      googleSyncStatus={googleSyncStatus}
      lastSynced={lastSynced}
      checkSyncStatus={checkSyncStatus}
    />
  );
};
 
export default ActivitySchedules;
ActivitySchedules.propTypes = {
  list: PropTypes.array,
  loading: PropTypes.bool,
  setDateFilter: PropTypes.func,
  setSearchQuery: PropTypes.func,
  setShowAll: PropTypes.func,
  showAll: PropTypes.bool,
  useDateFilter: PropTypes.bool,
  fetchActivitySessions: PropTypes.func,
  fetchMySchedule: PropTypes.func,
  fetchUserProduct: PropTypes.func,
  fetchNext7DaysSessionCount: PropTypes.func,
  setLoading: PropTypes.func,
};
 